import { memo } from "react";
import { ButtonBase, FormHelperText, IconButton, TextField, Tooltip } from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteOutlineIcon,
  CalendarIcon as CalendarTodayIcon,
} from "components/icons";
import MDBox from "components/MDBox";
import * as AppConstants from "AppConstants";
import dayjs from "dayjs";

/** Shared by the header and every row so the two can never drift out of alignment. */
// @since NEXT_VERSION
export const ENTRY_ROW_COLUMNS = "148px 216px minmax(0, 1fr) 114px 96px 72px 104px";

// Literal radius and CSS-variable colours rather than theme keys: the component suite renders
// without the app's ThemeProvider. The variables follow the base colour and light/dark mode.
const FIELD_RADIUS = "8px";
const FIELD_BORDER = "var(--border)";
const ERROR_BORDER = "var(--error)";

// @since NEXT_VERSION
const ROW_LIMIT_HINT = `Up to ${AppConstants.MAX_ACTIVITY_ROWS_PER_DIALOG} entries can be added at a time`;

/**
 * One row of the bulk entry table — one activity, not one day.
 *
 * Every mutation is reported upward through onChange/onAdd/onDuplicate/onRemove so the dialog stays
 * the single owner of the row list and validation runs in one place.
 *
 * @since NEXT_VERSION
 */
const EntryRow = ({
  row,
  index,
  errors = {},
  activityTypes = [],
  savedHours = 0,
  isOnlyRow = false,
  canAddRows = true,
  onChange,
  onAdd,
  onDuplicate,
  onRemove,
  onOpenPicker,
}) => {
  const humanIndex = index + 1;
  const dateLabel = row.date ? dayjs(row.date).format("ddd DD MMM") : "Pick a date";

  return (
    // Layout goes through sx, not props: MDBox forwards unknown props to the DOM, so
    // display="grid" and friends never reached the stylesheet and the row was not a grid at all.
    <MDBox
      sx={{
        display: "grid",
        gridTemplateColumns: ENTRY_ROW_COLUMNS,
        gap: 1,
        alignItems: "flex-start",
        px: 1.75,
        py: 1.25,
        borderTop: index > 0 ? "1px solid var(--border)" : "none",
      }}
    >
      <MDBox>
        <ButtonBase
          disableRipple
          onClick={(event) => onOpenPicker(index, event.currentTarget)}
          sx={{
            width: "100%",
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 0.75,
            px: 1,
            font: "inherit",
            fontSize: 13,
            fontWeight: row.date ? 600 : 400,
            color: row.date ? "var(--text)" : "var(--field-border)",
            backgroundColor: "var(--surface)",
            border: `1px solid ${errors.date ? ERROR_BORDER : FIELD_BORDER}`,
            borderRadius: FIELD_RADIUS,
          }}
        >
          <CalendarTodayIcon sx={{ fontSize: 14, color: "var(--text-2)", flexShrink: 0 }} />
          <span>{dateLabel}</span>
          {savedHours > 0 && (
            <MDBox component="span" sx={{ ml: "auto", fontSize: 9, fontWeight: 700, color: "var(--text-3)" }}>
              {savedHours}h
            </MDBox>
          )}
        </ButtonBase>
        {errors.date && <FormHelperText error>{errors.date}</FormHelperText>}
      </MDBox>

      <TextField
        size="small"
        fullWidth
        value={row.clientTicketRef ?? ""}
        placeholder="HRMS-300"
        onChange={(event) => onChange(index, { clientTicketRef: event.target.value })}
        slotProps={{
          htmlInput: {
            maxLength: AppConstants.MAX_CLIENT_TICKET_REF_LENGTH,
            "aria-label": `Task ticket for row ${humanIndex}`,
          },
        }}
      />

      <TextField
        size="small"
        fullWidth
        multiline
        // Two lines, then it scrolls: a row of the table stays a row, however long the
        // description gets. @since NEXT_VERSION
        maxRows={2}
        value={row.description ?? ""}
        placeholder="What was done"
        error={Boolean(errors.description)}
        helperText={errors.description}
        onChange={(event) => onChange(index, { description: event.target.value })}
        slotProps={{
          htmlInput: {
            maxLength: AppConstants.MAX_ACTIVITY_DESCRIPTION_LENGTH,
            "aria-label": `Description for row ${humanIndex}`,
          },
        }}
        // The theme pads a multiline root as well as the small input inside it, so this field
        // carried both and stood far taller than the fields beside it. Dropping the root's leaves
        // the input's own — the very padding those fields are drawn with. @since NEXT_VERSION
        sx={{ "& .MuiOutlinedInput-root": { padding: 0 } }}
      />

      <TextField
        select
        size="small"
        fullWidth
        value={row.activityTypeId ?? ""}
        error={Boolean(errors.activityTypeId)}
        helperText={errors.activityTypeId}
        onChange={(event) =>
          onChange(index, {
            activityTypeId: event.target.value === "" ? null : parseInt(event.target.value, 10),
          })
        }
        slotProps={{
          select: { native: true },
          htmlInput: { "aria-label": `Activity type for row ${humanIndex}` },
        }}
      >
        <option value="">Select</option>
        {activityTypes.filter(Boolean).map((type) => (
          <option key={type.id} value={type.id}>
            {type.activityDesc}
          </option>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        fullWidth
        value={row.status}
        onChange={(event) => onChange(index, { status: event.target.value })}
        slotProps={{
          select: { native: true },
          htmlInput: { "aria-label": `Status for row ${humanIndex}` },
        }}
      >
        {AppConstants.ACTIVITY_STATUSES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </TextField>

      <TextField
        size="small"
        fullWidth
        type="number"
        value={row.hoursSpent}
        error={Boolean(errors.hoursSpent)}
        helperText={errors.hoursSpent}
        // Stops the wheel silently changing hours while the row is focused.
        onWheel={(event) => event.target.blur()}
        onChange={(event) => {
          const raw = event.target.value;
          onChange(index, { hoursSpent: raw === "" ? "" : parseFloat(raw) });
        }}
        slotProps={{
          htmlInput: {
            // The browser steps from `min`, not from zero, so a min of 0.1 made the arrows walk
            // 0.1 → 0.6 → 1.1: a 0.1 jump off the bottom and 0.4 back down to it. Starting at half
            // an hour puts every arrow step on a clean 0.5. Typing a finer figure still works —
            // the hours validator, not this attribute, decides what may be saved.
            // @since NEXT_VERSION
            min: 0.5,
            max: AppConstants.MAXIMUM_ACTIVITY_HOURS_PER_DAY,
            step: 0.5,
            style: { textAlign: "center" },
            "aria-label": `Hours for row ${humanIndex}`,
          },
        }}
      />

      <MDBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, height: 34 }}>
        <Tooltip title={canAddRows ? "Add an empty row below" : ROW_LIMIT_HINT}>
          {/* A disabled IconButton fires no events, so the tooltip needs a wrapper that can. */}
          <span>
            <IconButton
              size="small"
              aria-label={`Add a row below row ${humanIndex}`}
              disabled={!canAddRows}
              onClick={() => onAdd(index)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canAddRows ? "Duplicate this row, same day" : ROW_LIMIT_HINT}>
          <span>
            <IconButton
              size="small"
              aria-label={`Duplicate row ${humanIndex}`}
              disabled={!canAddRows}
              onClick={() => onDuplicate(index)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isOnlyRow ? "The last row cannot be removed" : "Remove this row"}>
          <span>
            <IconButton
              size="small"
              aria-label={`Remove row ${humanIndex}`}
              disabled={isOnlyRow}
              onClick={() => onRemove(index)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </MDBox>
    </MDBox>
  );
};

export default memo(EntryRow);
